import { APIException } from '~/plugins/axios/lib/exception'
import { ApplicationService } from '~/services/core/application.service'
import { OrderAPIException } from '~/services/exceptions/orderAPI.exception'

type SalesStatisticsResponse = {
  total_savings: number,
  completed_orders: number,
}

type OrderStepResponse = {
  order_id: string,
  step: string,
  back_document: string,
  front_document:string,
}

// TODO: Update type when the backend will be ready
export type ValidDiscountPayload = Record<string, any>[]

// TODO:
//  - Create offer interfaces to use as return type
export class OrderService extends ApplicationService {
  get baseUrl () {
    return this.runtimeConfig.BASE_API_URL
  }

  async getOrderByOrderId (orderId: string) {
    try {
      return await this.get('/api/sales/get_sale_by_id/', { order_id: orderId })
    } catch (error) {
      throw new OrderAPIException('Unable to obtain a sale by ID', (error as APIException))
    }
  }

  async getOrderStepByOrderId (orderId: string): Promise<OrderStepResponse> {
    try {
      return await this.get('/api/order_step/', { order_id: orderId })
    } catch (error) {
      throw new OrderAPIException('Unable to obtain a sale step by ID', (error as APIException))
    }
  }

  async sendLinkViaSMS (orderId: string, phoneNumber: string) {
    try {
      return await this.post('/api/sales/send_link_to_mobile/', {
        order_id: orderId,
        phone_number: phoneNumber,
      })
    } catch (error) {
      throw new OrderAPIException('Unable to send link via SMS', (error as APIException))
    }
  }

  async salesStatistics (): Promise<SalesStatisticsResponse> {
    try {
      return await this.get('/api/sales/statistics')
    } catch (error) {
      throw new OrderAPIException('Unable to fetch sales statistics', (error as APIException))
    }
  }

  async requestCashbackByOrderId (payload: any) {
    try {
      return await this.post('/api/special_offers/request_cashback/', payload)
    } catch (error) {
      throw new OrderAPIException('Unable to submit a request cashback form', (error as APIException))
    }
  }

  async sendFeedback (orderId: string, payload: Record<string, string>) {
    try {
      return await this.post('/api/sales/set_feedback/', {
        order_id: orderId,
        ...payload,
      })
    } catch (error) {
      throw new OrderAPIException('Unable to provide a feedback', (error as APIException))
    }
  }

  async matchFace (orderId: string, frontSideDocument: string, backSideDocument: string, faceFilename: string) {
    try {
      return await this.post('/api/match_faces/', {
        order_id: orderId,
        doc_front_side: frontSideDocument,
        doc_back_side: backSideDocument,
        webcam_photo: faceFilename,
      })
    } catch (error) {
      const data = (error as APIException).response?.data || {}

      if (data.similarity && data.similarity < 70) {
        throw new OrderAPIException('Unable to match face. Similarity percent is too low', (error as APIException))
      }

      if (data.order_id && data.order_id.includes('The selfie is already passed')) {
        throw new OrderAPIException('ID verification has already been passed for this order', (error as APIException))
      }

      throw new OrderAPIException('Unknown error occurred during face matching procedure', (error as APIException))
    }
  }

  async getInvoiceUploadLink (orderId: string, documentName?: string) {
    try {
      return await this.get(`/api/sales/${orderId}/proof-documents/link`, {
        doc_name: documentName,
      })
    } catch (error) {
      throw new OrderAPIException('Unable to obtain a voucher upload link by ID', (error as APIException))
    }
  }

  async confirmInvoiceUploaded (orderId: string) {
    try {
      return await this.post(`/api/sales/${orderId}/proof-documents/link`)
    } catch (error) {
      throw new OrderAPIException('Unable to obtain a voucher upload link by ID', (error as APIException))
    }
  }
}
